<template>
  <div class="wpp" :class="{'wpp--compact': compact}">
    <div class="wpp__phone">
      <div class="wpp__top">
        <span class="wpp__avatar"><i class="fab fa-whatsapp"/></span>
        <div class="wpp__who">
          <span class="wpp__name">{{ sender || 'WhatsApp Business' }}</span>
          <span class="wpp__sub">{{ subtitleLine }}</span>
        </div>
      </div>

      <div class="wpp__screen">
        <div class="wpp__bubble">
          <!-- media / card image -->
          <div v-if="mediaUrls && mediaUrls.length" class="wpp__media">
            <img v-if="isImage(mediaUrls[0])" :src="mediaUrls[0]" alt="" @error="mediaBroken = true" v-show="!mediaBroken"/>
            <div v-if="!isImage(mediaUrls[0]) || mediaBroken" class="wpp__media-file">
              <i :class="mediaIcon(mediaUrls[0])"/>
              <span>{{ fileName(mediaUrls[0]) }}</span>
            </div>
          </div>

          <div v-if="header" class="wpp__header">{{ render(header) }}</div>
          <div v-if="title" class="wpp__title">{{ render(title) }}</div>
          <div v-if="subtitle" class="wpp__subtitle">{{ render(subtitle) }}</div>

          <!-- location -->
          <div v-if="type === 'LOCATION' || type === 'twilio/location'" class="wpp__location">
            <i class="fas fa-location-dot"/>
            <span>{{ render(body) || label }}</span>
          </div>
          <p v-else-if="body" class="wpp__text" v-html="formatted(render(body))"/>
          <p v-else-if="type === 'AUTHENTICATION' || type === 'whatsapp/authentication'" class="wpp__text">
            <em>{{ codeSample }}</em> is your verification code.
            <span v-if="securityRecommendation"> For your security, do not share this code.</span>
          </p>
          <p v-else class="wpp__text wpp__text--empty">{{ emptyText }}</p>

          <div v-if="footer" class="wpp__footer">{{ render(footer) }}</div>
          <span class="wpp__time">{{ time }}</span>
        </div>

        <!-- quick replies / call to action buttons -->
        <div v-if="visibleActions.length" class="wpp__actions" :class="{'wpp__actions--cta': isCta}">
          <button v-for="(action, i) in visibleActions" :key="i" type="button" class="wpp__action" disabled>
            <i :class="actionIcon(action)"/>{{ render(action.title || action.text) }}
          </button>
        </div>

        <!-- list picker -->
        <div v-if="items && items.length" class="wpp__list">
          <button type="button" class="wpp__action wpp__action--list" disabled>
            <i class="fas fa-list-ul"/>{{ button || 'Options' }}
          </button>
          <div class="wpp__list-items">
            <div v-for="(item, i) in items" :key="i" class="wpp__list-item">
              <span class="wpp__list-title">{{ render(item.item || item.title) }}</span>
              <span v-if="item.description" class="wpp__list-desc">{{ render(item.description) }}</span>
            </div>
          </div>
        </div>

        <!-- auth copy code -->
        <div v-if="(type === 'AUTHENTICATION' || type === 'whatsapp/authentication')" class="wpp__actions">
          <button type="button" class="wpp__action" disabled><i class="far fa-copy"/>{{ button || 'Copy code' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {renderVariables, replaceTags} from './whatsappUtils'

/**
 * A WhatsApp-styled rendering of one template / message so the user sees
 * what the guest will see: body with *bold* / _italic_, header, footer,
 * media, quick-reply & call-to-action buttons, list picker, location.
 * Variables ({{1}}) and #tags# are substituted when given.
 */
export default {
  name: 'WhatsappPhonePreview',
  props: {
    type: {type: String, default: 'TEXT'},
    body: {type: String, default: ''},
    header: {type: String, default: ''},
    footer: {type: String, default: ''},
    title: {type: String, default: ''},
    subtitle: {type: String, default: ''},
    button: {type: String, default: ''},
    label: {type: String, default: ''},
    mediaUrls: {type: Array, default: () => []},
    actions: {type: Array, default: () => []},
    items: {type: Array, default: () => []},
    /** {{n}} → value */
    variables: {type: Object, default: null},
    /** #tag# → guest value (applied after variables) */
    tags: {type: Object, default: null},
    sender: {type: String, default: ''},
    recipientLine: {type: String, default: ''},
    securityRecommendation: {type: Boolean, default: false},
    emptyText: {type: String, default: '…'},
    compact: {type: Boolean, default: false},
  },
  data: () => ({mediaBroken: false, codeSample: '482913'}),
  computed: {
    time() {
      return this.$moment().format('HH:mm');
    },
    subtitleLine() {
      return this.recipientLine || 'online';
    },
    isCta() {
      return this.type === 'CALL_TO_ACTION' || this.type === 'twilio/call-to-action';
    },
    visibleActions() {
      return (this.actions || []).filter(a => a && (a.title || a.text));
    },
  },
  watch: {
    mediaUrls() {
      this.mediaBroken = false;
    }
  },
  methods: {
    render(text) {
      return replaceTags(renderVariables(text, this.variables), this.tags);
    },
    /** WhatsApp markdown: *bold*, _italic_, ~strike~, ```mono``` */
    formatted(text) {
      const escaped = String(text || '')
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return escaped
          .replace(/```([^`]+)```/g, '<code>$1</code>')
          .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
          .replace(/_([^_\n]+)_/g, '<em>$1</em>')
          .replace(/~([^~\n]+)~/g, '<s>$1</s>')
          .replace(/(\{\{\s*[A-Za-z0-9_]+\s*}}|#[A-Za-z0-9_]+#)/g, '<mark>$1</mark>')
          .replace(/\n/g, '<br>');
    },
    isImage(url) {
      return /\.(png|jpe?g|gif|webp)(\?.*)?$/i.test(url || '') || /^data:image/.test(url || '');
    },
    mediaIcon(url) {
      if (/\.pdf(\?.*)?$/i.test(url || '')) return 'far fa-file-pdf';
      if (/\.(mp4|mov|3gp)(\?.*)?$/i.test(url || '')) return 'fas fa-film';
      if (/\.(mp3|ogg|aac|amr|opus)(\?.*)?$/i.test(url || '')) return 'fas fa-music';
      return 'far fa-file';
    },
    fileName(url) {
      try {
        const clean = String(url).split('?')[0];
        return clean.substring(clean.lastIndexOf('/') + 1) || url;
      } catch (e) {
        return url;
      }
    },
    actionIcon(action) {
      const type = String(action.type || '').toUpperCase();
      if (type === 'URL') return 'fas fa-arrow-up-right-from-square';
      if (type === 'PHONE_NUMBER') return 'fas fa-phone';
      if (type === 'COPY_CODE') return 'far fa-copy';
      return 'fas fa-reply';
    },
  }
}
</script>

<style lang="scss" scoped>
.wpp { display: flex; justify-content: center; }
.wpp__phone {
  width: 300px; max-width: 100%; border-radius: 26px; overflow: hidden;
  background: #efe7dd url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='%23000' fill-opacity='.035'%3E%3Ccircle cx='10' cy='10' r='2'/%3E%3Ccircle cx='40' cy='30' r='2'/%3E%3Ccircle cx='20' cy='48' r='2'/%3E%3C/g%3E%3C/svg%3E");
  border: 6px solid #1f2937; box-shadow: 0 12px 30px rgba(15, 23, 42, .18);
}
.wpp--compact .wpp__phone { width: 250px; border-width: 4px; border-radius: 20px; }
.wpp__top {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  background: #075e54; color: #fff;
}
.wpp__avatar {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: #25d366; color: #fff; font-size: 1rem;
}
.wpp__who { display: flex; flex-direction: column; min-width: 0; }
.wpp__name { font-size: .82rem; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wpp__sub { font-size: .66rem; opacity: .8; }
.wpp__screen { padding: 14px 10px 16px; min-height: 160px; }
.wpp__bubble {
  position: relative; background: #fff; border-radius: 10px; border-top-left-radius: 2px;
  padding: 8px 10px 18px; max-width: 92%; box-shadow: 0 1px 1px rgba(0, 0, 0, .08);
  color: #111b21; font-size: .8rem; line-height: 1.4; word-break: break-word;
}
.wpp__media { margin: -4px -6px 6px; border-radius: 8px; overflow: hidden; background: #e2e8f0; }
.wpp__media img { display: block; width: 100%; max-height: 160px; object-fit: cover; }
.wpp__media-file {
  display: flex; align-items: center; gap: 8px; padding: 12px 10px; color: #475569; font-size: .74rem;
  i { font-size: 1.3rem; color: #64748b; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}
.wpp__header { font-weight: 800; margin-bottom: 4px; }
.wpp__title { font-weight: 800; font-size: .86rem; }
.wpp__subtitle { color: #475569; font-size: .74rem; margin-bottom: 4px; }
.wpp__text { margin: 0; white-space: pre-wrap; }
.wpp__text--empty { color: #94a3b8; font-style: italic; }
.wpp__text :deep(mark) { background: rgba(37, 211, 102, .25); color: #075e54; padding: 0 3px; border-radius: 3px; font-weight: 700; }
.wpp__text :deep(code) { background: #f1f5f9; padding: 0 4px; border-radius: 3px; font-size: .74rem; }
.wpp__footer { margin-top: 6px; color: #667781; font-size: .68rem; }
.wpp__location { display: flex; align-items: center; gap: 8px; color: #075e54; font-weight: 700; i { font-size: 1.1rem; } }
.wpp__time { position: absolute; right: 8px; bottom: 4px; font-size: .6rem; color: #667781; }
.wpp__actions { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; max-width: 92%; }
.wpp__actions--cta { }
.wpp__action {
  width: 100%; border: none; border-radius: 8px; background: #fff; color: #00a5f4;
  font-size: .78rem; font-weight: 700; padding: 8px 10px; text-align: center;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, .08);
  &:disabled { opacity: 1; cursor: default; }
}
.wpp__list { margin-top: 4px; max-width: 92%; }
.wpp__list-items { margin-top: 6px; background: #fff; border-radius: 8px; overflow: hidden; }
.wpp__list-item { display: flex; flex-direction: column; padding: 7px 10px; border-bottom: 1px solid #f1f5f9; &:last-child { border-bottom: none; } }
.wpp__list-title { font-size: .78rem; font-weight: 700; color: #111b21; }
.wpp__list-desc { font-size: .68rem; color: #667781; }
</style>
